import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx21I2vPlusParams {
  /**
   * 输入参数对象
   */
  input?: {
    /**
     * 文本提示词，描述要生成的视频内容，长度不超过800字符
     */
    prompt?: string;
    /**
     * 图像URL，用于图像生成视频模型，支持HTTP/HTTPS URL或Base64编码图像
     */
    img_url: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 负面提示词，描述不希望出现在视频中的内容，长度不超过500字符
     */
    negative_prompt?: string;
    /**
     * 视频分辨率，wanx2.1-i2v-plus仅支持'720P'
     * @default "720P"
     * @values 720P
     */
    resolution?: '720P';
    /**
     * 视频时长，wanx2.1-i2v-plus固定为5秒，不可修改
     * @default 5
     */
    duration?: number;
    /**
     * 随机种子，用于可重现的生成结果，范围0-2147483647
     * @range 0 - 2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词扩展
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
  };
}

export interface AlibabaWanx21I2vPlusResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx21_i2v_plus
 *
 * 阿里云 Wanx2.1 I2V Plus 视频生成实现
 * 
 * Image-to-Video generation implementation for Alibaba Wanx2.1 I2V Plus API.
 * 
 * Model: wanx2.1-i2v-plus
 * Features: 图像生成视频 (Image-to-Video)
 * Duration: 5秒 (固定)
 * Resolution: 720P (仅支持)
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/image-to-video-api-reference
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx21I2vPlus } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx21I2vPlus({
 *   // 参数见 AlibabaWanx21I2vPlusParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx21I2vPlusParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx21I2vPlus(
  params: AlibabaWanx21I2vPlusParams,
): Promise<AlibabaWanx21I2vPlusResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx21_i2v_plus',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx21I2vPlusResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
